// src/components/ReferralDashboard.jsx
import {
  Box,
  Grid,
  VStack,
  Heading,
  useToast,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Flex,
} from "@chakra-ui/react";

import ReferralInvitePanel from "./ReferralInvitePanel";
import ReferralEmailInvite from "./ReferralEmailInvite";
import ReferralCreditStatus from "./ReferralCreditStatus";
import ReferralLeaderboard from "./ReferralLeaderboard";
import BonusCouponModal from "./BonusCouponModal";
import ReferralBonusDashboard from "./ReferralBonusDashboard";
import OAuthLogViewer from "./OAuthLogViewer";
import ReferralCreditHistory from "./ReferralCreditHistory";

import { exportToCSV } from "../utils/exportToCSV";

/**
 * ReferralDashboard orchestrates all referral-related cockpit modules.
 *
 * IMPORTANT:
 *  - All referral tables use the legacy integer PK (businesses.id)
 *  - DO NOT switch these to authId or business_uuid
 */
export default function ReferralDashboard({ businessId }) {
  const toast = useToast();

  const showDispatchQueue = () => {
    const steps = [
      { title: "Step 1: Queuing dispatch...", status: "info" },
      { title: "Step 2: Scheduling print...", status: "info" },
      { title: "Step 3: Estimating delivery window...", status: "info" },
      { title: "✅ Dispatch pipeline triggered", status: "success" },
    ];

    steps.forEach((s, i) =>
      toast({
        title: s.title,
        status: s.status,
        duration: 2000 + i * 500,
        isClosable: true,
      })
    );
  };

  const handleExport = async (table) => {
    try {
      await exportToCSV(table, businessId);
      toast({
        title: `Exported ${table} data`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error(`Export failed for ${table}:`, err);
      toast({
        title: `Failed to export ${table}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>
        🚀 Referral Dashboard
      </Heading>

      {/* Top grid: invite + credit status */}
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
        <VStack align="start" spacing={6}>
          <ReferralInvitePanel businessId={businessId} />
          <ReferralEmailInvite businessId={businessId} />
          <BonusCouponModal businessId={businessId} />
        </VStack>

        <ReferralCreditStatus businessId={businessId} />
      </Grid>

      <Divider my={8} />

      {/* Tabs for bonuses + logs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>🎁 Referral Bonuses</Tab>
          <Tab>🔐 OAuth Logs</Tab>
        </TabList>

        <TabPanels>
          {/* Bonuses + credit history */}
          <TabPanel>
            <Flex justify="flex-end" mb={2}>
              <Button
                size="sm"
                colorScheme="green"
                onClick={() => handleExport("referral_audit")}
              >
                Export Bonuses CSV
              </Button>
            </Flex>

            <ReferralBonusDashboard businessId={businessId} />

            <Box mt={8}>
              <ReferralCreditHistory businessId={businessId} />
            </Box>
          </TabPanel>

          {/* OAuth logs */}
          <TabPanel>
            <Flex justify="flex-end" mb={2}>
              <Button
                size="sm"
                colorScheme="gray"
                onClick={() => handleExport("oauth_log_view")}
              >
                Export Logs CSV
              </Button>
            </Flex>

            <OAuthLogViewer businessId={businessId} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Divider my={8} />

      {/* Dispatch toast demo */}
      <Button onClick={showDispatchQueue} colorScheme="blue" mt={4}>
        Trigger Dispatch Toast Queue
      </Button>

      {/* Leaderboard */}
      <Box mt={8}>
        <ReferralLeaderboard businessId={businessId} />
      </Box>
    </Box>
  );
}
